import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BuildingOfficeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { EmptyState } from '../components/common/EmptyState';
import { fetchReports, downloadReport, generateReportPDF, type ReportWithExpiry } from '../lib/apiClient';
import { formatDistanceToNow } from '../lib/time';
import { useAuth } from '../lib/auth';
import { PaywallModal } from '../components/billing/PaywallModal';

type ViewMode = 'grid' | 'list';

export function ReportsPage(): JSX.Element {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallMessage, setPaywallMessage] = useState<string | undefined>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['reports', user?.organizationId],
    queryFn: () => fetchReports({ limit: 50 }),
    enabled: Boolean(user),
  });

  const reports = useMemo(() => data?.data ?? [], [data]);

  const downloadMutation = useMutation({
    mutationFn: (reportId: string) => downloadReport(reportId),
    onSuccess: (data) => {
      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank');
      setLoadingReportId(null);
    },
    onError: (error) => {
      setLoadingReportId(null);
      if (isAxiosError(error) && error.response?.status === 402) {
        setPaywallMessage(error.response?.data?.message);
        setPaywallOpen(true);
      }
    },
  });

  const generateMutation = useMutation({
    mutationFn: (reportId: string) => generateReportPDF(reportId),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      // Auto-download after generation
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
      setLoadingReportId(null);
    },
    onError: (error) => {
      setLoadingReportId(null);
      if (isAxiosError(error) && error.response?.status === 402) {
        setPaywallMessage(error.response?.data?.message);
        setPaywallOpen(true);
      }
    },
  });

  const getPropertyInitials = (property: ReportWithExpiry['property']): string => {
    if (!property?.name) return '??';
    const words = property.name.split(' ');
    return words.slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
  };

  const getExpiryBadge = (report: ReportWithExpiry) => {
    if (!report.expiryInfo) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <SparklesIcon className="h-3 w-3" />
          Ready to generate
        </span>
      );
    }

    if (report.expiryInfo.expired) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <ExclamationTriangleIcon className="h-3 w-3" />
          Expired
        </span>
      );
    }

    if (report.expiryInfo.daysRemaining <= 1) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <ClockIcon className="h-3 w-3" />
          Expires soon
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
        <ClockIcon className="h-3 w-3" />
        {report.expiryInfo.daysRemaining} days left
      </span>
    );
  };

  const handleDownload = async (report: ReportWithExpiry) => {
    setLoadingReportId(report._id);
    
    // If PDF has never been generated OR has expired, generate it first
    if (!report.expiryInfo || report.expiryInfo.expired) {
      generateMutation.mutate(report._id);
    } else {
      // PDF exists and is valid - download it
      downloadMutation.mutate(report._id);
    }
  };

  const getButtonText = (report: ReportWithExpiry) => {
    if (loadingReportId === report._id) {
      return !report.expiryInfo || report.expiryInfo.expired ? 'Generating...' : 'Loading...';
    }
    if (!report.expiryInfo) {
      return 'Generate PDF';
    }
    if (report.expiryInfo.expired) {
      return 'Regenerate';
    }
    return 'Download';
  };

  const getButtonIcon = (report: ReportWithExpiry) => {
    if (loadingReportId === report._id) {
      return <ArrowPathIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />;
    }
    if (!report.expiryInfo) {
      return <SparklesIcon className="mr-1.5 h-3.5 w-3.5" />;
    }
    if (report.expiryInfo.expired) {
      return <ArrowPathIcon className="mr-1.5 h-3.5 w-3.5" />;
    }
    return <ArrowDownTrayIcon className="mr-1.5 h-3.5 w-3.5" />;
  };

  return (
    <div className="space-y-6">
      <header className="glass-card flex flex-wrap items-center justify-between gap-4 p-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Reports</h1>
          <p className="text-sm text-muted-foreground">
            View and download your inspection reports. Reports are available for 7 days after generation.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-border bg-white/50 p-1 dark:bg-white/5">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Grid
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              List
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate('/app/items')}
            className="btn-glass btn-primary focus-ring text-xs font-semibold"
          >
            New inspection
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="glass-card h-48 animate-pulse" />
          ))}
        </div>
      ) : reports.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {reports.map((report) => (
              <article
                key={report._id}
                className="glass-card group relative flex flex-col overflow-hidden transition-shadow hover:shadow-lg"
              >
                {/* Property Thumbnail / Initials */}
                <div className="relative h-32 bg-gradient-to-br from-primary/20 to-primary/5">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/80 text-2xl font-bold text-primary shadow-lg dark:bg-gray-800">
                      {getPropertyInitials(report.property)}
                    </div>
                  </div>
                  <div className="absolute right-3 top-3">
                    {getExpiryBadge(report)}
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-semibold text-foreground line-clamp-1">
                    {report.property?.name || 'Unknown Property'}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-1">
                    {report.property?.address?.line1}, {report.property?.address?.city}
                  </p>

                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <DocumentTextIcon className="h-3.5 w-3.5" />
                      v{report.currentVersion}
                    </span>
                    <span className="flex items-center gap-1">
                      <BuildingOfficeIcon className="h-3.5 w-3.5" />
                      {report.inspection?.roomsCount || 0} rooms
                    </span>
                  </div>

                  <p className="mt-2 text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(report.updatedAt)}
                  </p>

                  <div className="mt-auto flex gap-2 pt-4">
                    <button
                      type="button"
                      onClick={() => handleDownload(report)}
                      disabled={loadingReportId === report._id}
                      className="btn-glass btn-primary focus-ring flex flex-1 items-center justify-center text-xs font-semibold disabled:opacity-50"
                    >
                      {getButtonIcon(report)}
                      {getButtonText(report)}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const inspectionId = typeof report.inspectionId === 'string'
                          ? report.inspectionId
                          : report.inspection?._id;
                        if (inspectionId) {
                          navigate(`/app/items/${inspectionId}`);
                        }
                      }}
                      className="btn-glass btn-secondary focus-ring text-xs font-semibold"
                    >
                      View
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <article
                key={report._id}
                className="glass-card flex items-center justify-between gap-4 p-4"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary">
                    {getPropertyInitials(report.property)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {report.property?.name || 'Unknown Property'}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {report.property?.address?.line1}, {report.property?.address?.city} · 
                      {report.inspection?.roomsCount || 0} rooms · v{report.currentVersion}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getExpiryBadge(report)}
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(report.updatedAt)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDownload(report)}
                    disabled={loadingReportId === report._id}
                    className="btn-glass btn-primary focus-ring flex items-center text-xs font-semibold disabled:opacity-50"
                  >
                    {loadingReportId === report._id && (
                      <ArrowPathIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    )}
                    {getButtonText(report)}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const inspectionId = typeof report.inspectionId === 'string'
                        ? report.inspectionId
                        : report.inspection?._id;
                      if (inspectionId) {
                        navigate(`/app/items/${inspectionId}`);
                      }
                    }}
                    className="btn-glass btn-secondary focus-ring text-xs font-semibold"
                  >
                    View
                  </button>
                </div>
              </article>
            ))}
          </div>
        )
      ) : (
        <EmptyState
          title="No reports yet"
          description="Complete an inspection and generate a report to see it here."
          actionLabel="Start inspection"
          onAction={() => navigate('/app/items?action=new-inspection')}
        />
      )}

      {data && data.meta.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {[...Array(data.meta.totalPages)].map((_, i) => (
            <button
              key={i}
              type="button"
              className={`h-8 w-8 rounded-lg text-xs font-medium ${
                i + 1 === data.meta.page
                  ? 'bg-primary text-white'
                  : 'bg-white/50 text-muted-foreground hover:bg-white/80 dark:bg-white/10'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        message={paywallMessage}
      />
    </div>
  );
}
